import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { keysToTranslate } from 'i18n/keys-to-translate';
import { FormatConfigService } from '../../core/services/format-config.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AbstractComponent } from '../../shared/components/abstract-component/abstract.component';
import { GrundstueckListEntry } from '../../core/av-api';
import { DokulinkItemBuilderFactory } from '../../shared/components/dokulink/dokulinkItemBuilderFactory';
import { BelegEntry, GrundstueckDetailEntryMeta, GrundstueckNummerListEntry } from 'app/core/gb-api/model/models';
import { ContentHeaderComponent } from '../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../shared/components/title/title.component';
import {
  KompaktGrundstueckDetailComponent
} from '../../shared/kompaktdarstellung/grundstueck/grundstueck-detail/kompakt-grundstueck-detail.component';
import { TitleFunctionbarComponent } from '../../shared/components/title-functionbar/title-functionbar.component';
import { MapsComponent } from '../../shared/widgets/maps/maps.component';
import { DokulinkComponent } from '../../shared/components/dokulink/dokulink.component';
import { AuszugComponent } from '../../shared/components/auszug/auszug.component';
import { KopfdatenelementComponent } from '../../shared/widgets/kopfdatenelement/kopfdatenelement.component';
import { LinkComponent } from '../../shared/components/link/link.component';
import { KompaktTimepointComponent } from '../../shared/kompaktdarstellung/timepoint/kompakt-timepoint.component';
import { DatumPipe } from '../../shared/pipes/simple/datum.pipe';
import { GrundstueckArtLangPipe } from '../../shared/pipes/simple/grundstueck-art-lang.pipe';
import { TranslateNamePipe } from '../../shared/pipes/simple/translate-name.pipe';
import { MapType } from '../../shared/enum/map-type';
import {
  OerebTitleActionButtonsComponent
} from '../../oereb/oereb-title-action-buttons/oereb-title-action-buttons.component';
import { ErweiterungConfigResponse, PermissionResponse } from '../../core/base-api';
import { GeolinkMapViewerService } from '../../shared/services/geolink-map-viewer.service';
import { SafeResourceUrl } from '@angular/platform-browser';
import { combineLatest, Subscription } from 'rxjs';
import { AppPermissionService } from '../../core/services/app-permission.service';
import DokulinkVerfuegbarEnum = BelegEntry.DokulinkVerfuegbarEnum;
import GrundbuchFuehrungEnum = GrundstueckDetailEntryMeta.GrundbuchFuehrungEnum;
import ActiveErweiterungenEnum = ErweiterungConfigResponse.ActiveErweiterungenEnum;
import PermissionsEnum = PermissionResponse.PermissionsEnum;

@Component({
  selector: 'cap-grundstueck-kopf',
  templateUrl: './grundstueck-kopf.component.html',
  styleUrls: ['./grundstueck-kopf.component.scss'],
  imports: [
    ContentHeaderComponent,
    TitleComponent,
    KompaktGrundstueckDetailComponent,
    TitleFunctionbarComponent,
    MapsComponent,
    DokulinkComponent,
    AuszugComponent,
    KopfdatenelementComponent,
    LinkComponent,
    KompaktTimepointComponent,
    TranslatePipe,
    DatumPipe,
    GrundstueckArtLangPipe,
    TranslateNamePipe,
    OerebTitleActionButtonsComponent
  ]
})
export class GrundstueckKopfComponent extends AbstractComponent implements OnInit, AfterViewInit, OnDestroy {
  gsEntry: GrundstueckDetailEntryMeta;
  gsNummerEntry: GrundstueckNummerListEntry;
  loaded = false;
  gsNummer: string;

  permissionAuftragsmodul: boolean = false; // Erweiterung erst mit CGE prüfen
  mapsVerfuegbar = true;
  dokulinkVerfuegbar: DokulinkVerfuegbarEnum = 'DOKULINK_VORHANDEN';
  isDownloading: boolean = false;
  oerebVerfuegbar: boolean = false;
  protected readonly mapType = MapType;

  private subscription: Subscription = new Subscription();
  @ViewChild('iframeRef') iframeRef: ElementRef<HTMLIFrameElement>;
  isMapViewerErlaubt = false;
  isMapLigAllowed: boolean = false;
  isMapSdrNichtAusgeschiedenAllowed: boolean = false;
  isMapSdrAusgeschiedenAllowed: boolean = false;

  protected safeUrl: SafeResourceUrl;
  protected hasPermissionGrundstueckPdfLink: boolean = false;

  constructor(
    protected appFormatConfigService: FormatConfigService,
    protected permissionService: AppPermissionService,
    private readonly translate: TranslateService,
    public dokuLinkBuilderFactory: DokulinkItemBuilderFactory,
    private readonly cd: ChangeDetectorRef,
    private readonly geoLinkMapViewerService: GeolinkMapViewerService
  ) {
    super();
    keysToTranslate<string>([
      'GeschlossenAm',
      'Auftraege',
      'FormDerFuehrung',
      'Grundbuchtyp',
      'GrundstueckArt',
      'GrundstueckNummer',
      'ErstelltMit',
      'GestrichenMit',
      'HaengigeGeschaefteAufträge',
      'HaengigeGeschaefte',
      'GrundstueckKopf.Label.Gemeinde'
    ]);
    keysToTranslate<GrundbuchFuehrungEnum>(['EIDGENOESSISCH', 'KANTONAL', 'BEREINIGUNG', 'EINFUEHRUNG']);

    this.subscriptions.push(
      combineLatest([this.appFormatConfigService.erweiterungsConfig$, this.appFormatConfigService.parameterConfig$]).subscribe(
        ([erweiterungsConfig, parameterConfig]) => {
          this.isMapViewerErlaubt = erweiterungsConfig?.activeErweiterungen.includes(ActiveErweiterungenEnum.EINBINDUNG_KARTEN_AUS_GIS);
          this.isMapLigAllowed = !!parameterConfig?.parameters.LIEGENSCHAFT_MIT_GEO_LINK;
          this.isMapSdrAusgeschiedenAllowed = !!parameterConfig?.parameters.SDR_AUSGESCHIEDEN_MIT_GEO_LINK;
          this.isMapSdrNichtAusgeschiedenAllowed = !!parameterConfig?.parameters.SDR_NICHT_AUSGESCHIEDEN_MIT_GEO_LINK;
          this.oerebVerfuegbar = erweiterungsConfig?.activeErweiterungen.includes(ActiveErweiterungenEnum.OEREB_CH);
        }
      )
    );
  }

  @Input()
  set grundstueckEntry(value: GrundstueckDetailEntryMeta) {
    this.gsEntry = value;
    this.calculateStringRepresentation();
  }

  ngOnInit(): void {
    this.calculateStringRepresentation();
    this.subscription.add(
      this.appFormatConfigService.settingsConfig$.subscribe((settingsConfig) => {
        this.mapsVerfuegbar = settingsConfig?.isMapsLinkConfigured;
      })
    );
    this.hasPermissionGrundstueckPdfLink = this.permissionService.hasPermission(PermissionsEnum.GRUNDSTUECK_PDF_LINK);
  }

  ngAfterViewInit(): void {
    this.initMap();
    this.cd.detectChanges();
  }

  private initMap() {
    if (this.isMapViewerAllowed()) {
      const htmlContent = this.geoLinkMapViewerService.getMapViewerHtmlContent(this.gsEntry.egrid.value, 'dmz', 'ch.swisstopo.swissimage');
      this.safeUrl = this.geoLinkMapViewerService.getMapBlobUrl(htmlContent);
    }
  }

  setShowDokulink(event: DokulinkVerfuegbarEnum): void {
    this.dokulinkVerfuegbar = event;
  }

  asListEntry(detailEntry: GrundstueckDetailEntryMeta): GrundstueckListEntry {
    return <GrundstueckListEntry>{
      id: detailEntry.id,
      grundstueckArt: this.gsEntry.grundstueckArt,
      grundstueckNummer: detailEntry.grundstueckNummern[detailEntry.grundstueckNummern.length - 1]
    };
  }

  private isMapViewerAllowed(): boolean {
    return this.isMapViewerErlaubt && this.isMapViewerForGrundstueckArtAllowed() && this.isMapViewerForGrundstueckTypeAllowed();
  }

  private isMapViewerForGrundstueckTypeAllowed() {
    return !this.gsEntry.isKopie && !this.gsEntry.histInfo?.bisTimePoint?.zeitpunktRechtsgueltig;
  }

  private isMapViewerForGrundstueckArtAllowed(): boolean {
    return (
      (this.gsEntry.grundstueckArt === 'LIEGENSCHAFT' && this.isMapLigAllowed) ||
      (this.gsEntry.grundstueckArt === 'SDR' && this.isMapViewerForSdrSubtypeAllowed())
    );
  }

  private isMapViewerForSdrSubtypeAllowed(): boolean {
    return (
      (this.gsEntry.flaechenMaessigNichtAusgeschieden && this.isMapSdrNichtAusgeschiedenAllowed) ||
      (!this.gsEntry.flaechenMaessigNichtAusgeschieden && this.isMapSdrAusgeschiedenAllowed)
    );
  }

  private combineIfNotNull(delimiter: string, nummer: any): string {
    if (nummer) {
      return delimiter + nummer.toString();
    }
    return '';
  }

  private calculateStringRepresentation(): void {
    this.gsNummerEntry = this.gsEntry.grundstueckNummern[this.gsEntry.grundstueckNummern.length - 1];
    this.gsNummer = this.gsNummerEntry.nummer;
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummerZusatz);
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummer3);
    this.gsNummer += this.combineIfNotNull('-', this.gsNummerEntry.nummer4);
    this.gsNummer += this.gsEntry.isKopie ? ' (' + this.translate.instant('GrundstückKopie') + ')' : '';
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
